// Remplacement simple de throttle pour éviter la dépendance lodash
const throttle = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  let lastExecTime = 0;
  return (...args: any[]) => {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func(...args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
};

interface BotDetectionResult {
  isBot: boolean;
  confidence: number;
  reasons: string[];
}

class AntiScrapingOptimized {
  private startTime: number;
  private mouseMovements: Array<{ x: number; y: number; timestamp: number }> = [];

  constructor() {
    this.startTime = Date.now();
    this.handleMouseMove = throttle(this.handleMouseMove.bind(this), 200);
  }

  /**
   * Initialise les écouteurs d'événements simplifiés
   */
  init(): void {
    // Seulement les événements essentiels
    document.addEventListener('contextmenu', this.handleContextMenu);
    document.addEventListener('copy', this.handleCopy);
    document.addEventListener('mousemove', this.handleMouseMove);
    
    // Créer le honeypot
    this.createHoneypot();
  }

  /**
   * Nettoie les écouteurs d'événements
   */
  cleanup(): void {
    document.removeEventListener('contextmenu', this.handleContextMenu);
    document.removeEventListener('copy', this.handleCopy);
    document.removeEventListener('mousemove', this.handleMouseMove);
  }

  /**
   * Détection de bot simplifiée (optimisée pour les performances)
   */
  detectBot(): BotDetectionResult {
    const reasons: string[] = [];
    let confidence = 0;

    // Check 1: User-Agent (le plus important)
    const userAgent = navigator.userAgent.toLowerCase();
    const botPatterns = ['bot', 'crawler', 'spider', 'scraper', 'headless'];
    
    if (botPatterns.some(pattern => userAgent.includes(pattern))) {
      reasons.push('Bot user agent detected');
      confidence += 80;
    }

    // Check 2: Honeypot (rapide)
    const honeypotField = document.querySelector('.honeypot') as HTMLInputElement;
    if (honeypotField && honeypotField.value) {
      reasons.push('Honeypot filled');
      confidence += 50;
    }

    return {
      isBot: confidence >= 50,
      confidence,
      reasons,
    };
  }

  /**
   * Obfuscation simple
   */
  obfuscate(text: string): string {
    return btoa(encodeURIComponent(text));
  }

  /**
   * Désobfuscation simple
   */
  deobfuscate(encodedText: string): string {
    try {
      return decodeURIComponent(atob(encodedText));
    } catch (e) {
      return '';
    }
  }

  /**
   * Protection d'élément simplifiée
   */
  protectElement(element: HTMLElement): void {
    element.style.userSelect = 'none';
    (element.style as any).webkitUserSelect = 'none';
    (element.style as any).mozUserSelect = 'none';
    (element.style as any).msUserSelect = 'none';
  }

  /**
   * Affiche un avertissement simple
   */
  showWarning(message: string): void {
    // Notification simple sans animations complexes
    const notification = document.createElement('div');
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #ffc107;
      color: #333;
      padding: 10px 20px;
      border-radius: 5px;
      font-size: 14px;
      z-index: 9999;
      opacity: 0.9;
    `;
    document.body.appendChild(notification);

    // Supprimer après 2 secondes
    setTimeout(() => notification.remove(), 2000);
  }

  /**
   * Gestionnaires d'événements simplifiés
   */
  private handleContextMenu = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.no-context-menu') || target.closest('.protected-text')) {
      e.preventDefault();
      this.showWarning('⚠️ Clic droit désactivé');
    }
  };

  private handleCopy = (e: ClipboardEvent) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      const selectedText = selection.toString();
      if (this.isProtectedText(selectedText)) {
        e.preventDefault();
        this.showWarning('⚠️ Contenu protégé');
      }
    }
  };

  private handleMouseMove = (e: MouseEvent) => {
    this.mouseMovements.push({
      x: e.clientX,
      y: e.clientY,
      timestamp: Date.now()
    });

    // Garder seulement les 10 derniers mouvements pour économiser la mémoire
    if (this.mouseMovements.length > 10) {
      this.mouseMovements.shift();
    }
  };

  private createHoneypot(): void {
    if (!document.querySelector('.honeypot')) {
      const honeypot = document.createElement('input');
      honeypot.type = 'text';
      honeypot.name = 'website';
      honeypot.className = 'honeypot';
      honeypot.style.cssText = 'position: absolute; left: -9999px; opacity: 0;';
      document.body.appendChild(honeypot);
    }
  }

  private isProtectedText(text: string): boolean {
    // Patterns simplifiés
    const patterns = [
      /[\w.-]+@[\w.-]+\.\w+/g, // Emails
      /(\+33|0)[1-9]\d{8}/g,   // Téléphones
      /https?:\/\/[\w.-]+/g     // URLs
    ];
    
    return patterns.some(pattern => pattern.test(text));
  }
}

export const antiScrapingOptimized = new AntiScrapingOptimized();
